import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, analytics } from "@/lib/utils";
import { getTrackingFields } from "@/lib/leadTracking";
import { getLocationDisplayName, LOCATION_FORM_ORDER } from "@/lib/locationUrls";
import { SEO } from "@/components/SEO";
import { TurnstileWidget } from "@/components/TurnstileWidget";

export default function CancellationPage() {
  const [locations, setLocations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    location_id: "",
    membership_identifier: "",
    reason: "",
    notes: "",
    acknowledgement: false,
    website: "", // honeypot
  });

  const cancellationReasons = [
    "Moving out of area",
    "Financial reasons",
    "Not using the gym enough",
    "Switching to another gym",
    "Health/medical reasons",
    "Dissatisfied with services",
    "Schedule no longer allows time for gym",
    "Injury or physical limitation",
    "Found a gym closer to home or work",
    "Temporary relocation or travel",
    "No longer need membership",
    "Other"
  ];

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website?.trim()) return; // honeypot
    if (!turnstileToken) {
      toast.error("Please complete the verification.");
      return;
    }
    if (!formData.full_name || !formData.email || !formData.phone || !formData.location_id || !formData.membership_identifier?.trim() || !formData.reason) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.acknowledgement) {
      toast.error("Please acknowledge the cancellation terms");
      return;
    }

    setIsSubmitting(true);

    try {
      const { website, ...rest } = formData;
      const response = await axios.post(`${API_URL}/cancellation`, { ...rest, cf_turnstile_response: turnstileToken, tracking: getTrackingFields() });
      analytics.trackCancelSubmit(formData.location_id);
      setIsSubmitted(true);
      toast.success("Cancellation request submitted successfully!");
    } catch (error) {
      console.error("Failed to submit cancellation request:", error);
      const msg = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(msg);
      if (error.response?.status === 400) setTurnstileToken("");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="cancellation-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">REQUEST RECEIVED</h2>
          <p className="text-gray-600 mb-4">
            Your cancellation request has been submitted. You will receive a confirmation email shortly.
          </p>
          <p className="text-sm text-gray-700 mb-8">
            Our team will process your request and contact you if any additional information is needed.
          </p>
          <Button 
            onClick={() => window.location.href = "/"}
            className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase tracking-wider"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="cancellation-page">
      <SEO
        title="Cancellation Request"
        description="Submit a cancellation request for your Giant Fitness membership. Fill out our form and a team member will contact you to complete the process. Membership agreement terms apply."
        url="/cancel"
        noindex={true}
      />
      {/* Header */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-4" data-testid="cancellation-heading">
              CANCELLATION REQUEST
            </h1>
            <p className="text-gray-400 text-lg">
              We're sorry to see you go. Please fill out the form below to request a membership cancellation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-xl mx-auto">
          {/* Warning Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3"
            data-testid="cancellation-warning"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-500 font-medium mb-1">Important Notice</p>
              <p className="text-gray-700">
                Please review your membership agreement for any cancellation terms, notice periods, or fees that may apply.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="cancellation-form">
              <div className="absolute -left-[9999px] w-1 h-1 overflow-hidden" aria-hidden="true">
                <Label htmlFor="cancellation-website">Website</Label>
                <Input
                  id="cancellation-website"
                  name="website"
                  type="text"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="full_name" className="form-label">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                  placeholder="Your full name as on membership"
                  data-testid="cancellation-name-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                    placeholder="your@email.com"
                    data-testid="cancellation-email-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="phone" className="form-label">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                    placeholder="(123) 456-7890"
                    data-testid="cancellation-phone-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="location" className="form-label">Home Club / Location *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, location_id: value })} required>
                  <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900" data-testid="cancellation-location-select">
                    <SelectValue placeholder="Select your home club" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {[...locations]
                      .sort((a, b) => LOCATION_FORM_ORDER.indexOf(a.id) - LOCATION_FORM_ORDER.indexOf(b.id))
                      .map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-gray-900 hover:bg-gray-100 focus:bg-gray-100">
                          {getLocationDisplayName(loc.id) ?? loc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="membership_identifier" className="form-label">Full Barcode or Membership Agreement Number *</Label>
                <Input
                  id="membership_identifier"
                  name="membership_identifier"
                  type="text"
                  value={formData.membership_identifier}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                  placeholder="e.g., 123456789012 or MA-2024-001234"
                  data-testid="cancellation-membership-input"
                />
                <p className="text-xs text-gray-600 mt-1">Enter your full membership barcode or your membership agreement number (required to process your request).</p>
              </div>

              <div className="form-group">
                <Label htmlFor="reason" className="form-label">Reason for Cancellation *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, reason: value })} required>
                  <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900" data-testid="cancellation-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {cancellationReasons.map((reason) => (
                      <SelectItem key={reason} value={reason} className="text-gray-900 hover:bg-gray-100 focus:bg-gray-100">
                        {reason}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="notes" className="form-label">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  className="bg-white border-gray-300 focus:border-brand-orange text-gray-900 resize-none"
                  placeholder="Any additional information you'd like to share"
                  data-testid="cancellation-notes-input"
                />
              </div>

              <div className="flex items-start space-x-3 p-4 bg-gray-100 border border-gray-200 rounded-lg">
                <Checkbox
                  id="acknowledgement"
                  checked={formData.acknowledgement}
                  onCheckedChange={(checked) => setFormData({ ...formData, acknowledgement: checked })}
                  className="border-neutral-600 data-[state=checked]:bg-brand-orange data-[state=checked]:border-brand-orange mt-0.5"
                  data-testid="cancellation-acknowledgement"
                />
                <Label htmlFor="acknowledgement" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                  I understand that my submission of a cancellation request is not a confirmation of a cancelled membership and that someone will reach out to me to assist in completing the process. I acknowledge that the terms of my membership agreement will apply.
                </Label>
              </div>

              <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase tracking-wider shadow-neon-orange"
                data-testid="cancellation-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </span>
                ) : (
                  "Submit Cancellation Request"
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
